<template>
    <div class="product-details" :class="{ ieMode: isIE }">
        <div class="details-container">
            <div class="cover-image" :style="{ 'background-image': 'url(' + product.image_url + ')' }">
                <product-tags :tags="product.tags"></product-tags>
                <div class="zoom" @click="showLightbox = true">
                    <svg-icon icon-holder-class="zoom-icon" icon-id="icon-zoom"></svg-icon>
                </div>
            </div>
            <div class="details">
                <div class="title" data-cy="product-title">
                    <span>{{ product.name }}</span>
                </div>
                <div ref="description" class="description" :class="{ expanded: isDescriptionExpanded }">
                    <span>{{ product.description }}</span>
                </div>
                <div v-if="expandableDescription" class="expand-description" @click="toggleDescription">
                    <div class="expand-description-icon" :class="{ expanded: isDescriptionExpanded }"></div>
                </div>
                <div class="commerce">
                    <div class="price" :class="{ 'single-product': product.bundles.length === 0 }">
                        <div class="amounts" data-cy="item-amount">
                            <span v-if="hasDiscount" class="discount">{{ displaySalePrice }}</span>
                            <span :class="{ discounted: hasDiscount }">{{ displayListPrice }}</span>
                        </div>
                        <div v-if="product.bundles.length === 0" class="quantities" :class="{ ieMode: isIE }">
                            <div class="quantity-container">
                                <div
                                    :class="{ disabled: quantity <= 1 }"
                                    class="quantity-decrease"
                                    data-cy="quantity-decrease"
                                    @click="decreaseQuantity"
                                >
                                    <svg-icon
                                        icon-holder-class="quantity-icon-decrease"
                                        icon-id="icon-minus"
                                    ></svg-icon>
                                </div>
                                <div class="quantity" data-cy="quantity">{{ quantity }}</div>
                                <div
                                    :class="{ disabled: quantity >= product.restrictions.max_item_quantity }"
                                    class="quantity-increase"
                                    data-cy="quantity-increase"
                                    @click="increaseQuantity"
                                >
                                    <svg-icon icon-holder-class="quantity-icon-increase" icon-id="icon-plus"></svg-icon>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="product.bundles.length === 0" class="total-price">
                        <div class="total">
                            <span>{{ $t('shop.total') }}</span>
                        </div>
                        <div class="total-amount" data-cy="total-amount">
                            <span>{{ displayTotalPrice }}</span>
                        </div>
                    </div>
                    <FormButton
                        data-cy="buy-button"
                        :button-click="handleBuyNowClick"
                        :label="$t('shop.buy-now')"
                        :loading="buttonLoading"
                    ></FormButton>
                </div>
            </div>
        </div>
        <div v-if="product && product.bundles.length > 0" class="bundles-container" data-cy="bundles-container">
            <span class="title">{{ $t('shop.items-included') }}</span>
            <div class="bundle-list-container">
                <div class="bundle-list-row">
                    <div v-for="(bundle, $index) in product.bundles" :key="bundle.productId" class="bundle-container">
                        <div class="bundle">
                            <div
                                class="bundle-image"
                                :style="{ 'background-image': 'url(' + bundle.image_url + ')' }"
                                :class="{ dimmed: bundleHoverIndex >= 0 && bundleHoverIndex !== $index }"
                            >
                                <div
                                    class="bundle-description"
                                    @click="
                                        bundleHoverIndex === $index
                                            ? (bundleDescriptionSticky = !bundleDescriptionSticky)
                                            : (bundleHoverIndex = $index)
                                    "
                                >
                                    <v-popover
                                        trigger="manual"
                                        placement="top-start"
                                        offset="5"
                                        delay="0"
                                        :container="getTooltipContainer"
                                        :open="bundleHoverIndex === $index"
                                        :auto-hide="false"
                                    >
                                        <div
                                            class="anchor-icon"
                                            @mouseover="bundleDescriptionSticky ? null : (bundleHoverIndex = $index)"
                                            @mouseleave="bundleDescriptionSticky ? null : (bundleHoverIndex = -1)"
                                        >
                                            <svg-icon
                                                icon-holder-class="read-more-icon"
                                                icon-id="icon-read-more"
                                            ></svg-icon>
                                        </div>

                                        <template slot="popover">
                                            <span>{{ bundle.description }}</span>
                                            <img
                                                class="tooltip-close"
                                                :src="closeButtonSrc"
                                                @click="[(bundleHoverIndex = -1), (bundleDescriptionSticky = false)]"
                                            />
                                        </template>
                                    </v-popover>
                                </div>
                            </div>
                            <div class="bundle-details">{{ bundle.name }} ({{ bundle.quantity }})</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showLightbox" class="lightbox">
            <div class="lightbox-container">
                <div :style="{ 'background-image': 'url(' + product.image_url + ')' }" class="lightbox-image"></div>
                <div @click="showLightbox = false">
                    <svg-icon icon-holder-class="lightbox-close" icon-id="icon-close"></svg-icon>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FormButton } from '@arena/toolkit';
import { mapState } from 'vuex';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import productTags from '@/components/common/product-tags';
import config from '@/config/environment-config';

export default {
    name: 'ProductDetails',
    components: {
        FormButton,
        svgIcon,
        productTags,
    },
    props: {
        product: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            showLightbox: false,
            quantity: 1,
            isDescriptionExpanded: false,
            expandableDescription: false,
            buttonLoading: false,
            bundleHoverIndex: -1,
            bundleDescriptionSticky: false,
        };
    },
    computed: {
        ...mapState(['service', 'category']),
        isIE() {
            return this.$store.state.isIE;
        },
        userData() {
            return this.$store.state.userData;
        },
        imgHost() {
            return `${process.env.BASE_URL}static/images/`;
        },
        closeButtonSrc() {
            return `${this.imgHost}bt_close5.png`;
        },
        storeBackground() {
            return `${this.imgHost}products/${this.service.service_name}/${this.service.service_name}_bg.png`;
        },
        currency() {
            return this.product.currency;
        },
        totalPrice() {
            return this.quantity * this.realPrice;
        },
        realPrice() {
            return this.hasDiscount ? this.product.sale_price : this.product.list_price;
        },
        displayTotalPrice() {
            return this.displayCurrency(this.totalPrice);
        },
        hasDiscount() {
            return this.product.sale_price < this.product.list_price;
        },
        displaySalePrice() {
            return this.displayCurrency(this.product.sale_price);
        },
        displayListPrice() {
            return this.displayCurrency(this.product.list_price);
        },
        getTooltipContainer() {
            return document.querySelector('.store-content');
        },
    },
    mounted() {
        document.body.scrollTop = 0;
        dataLayer.push({
            event: 'productDetailView',
            ecommerce: {
                detail: {
                    products: [
                        {
                            name: this.product.name,
                            id: this.product.no,
                            brand: this.service.service_name,
                            category: `${this.service.service_name}-${this.category.no}`,
                        },
                    ],
                },
            },
        });
        this.$nextTick(() => {
            window.addEventListener('resize', this.resetDescriptionScroll);

            this.resetDescriptionScroll();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.resetDescriptionScroll);
    },
    methods: {
        resetDescriptionScroll() {
            this.$refs.description.scrollTop = 0;
            this.expandableDescription = this.$refs.description.scrollHeight > 80;
        },
        handleBuyNowClick() {
            if (this.buttonLoading === true) return;
            if (!this.userData.is_authenticated) {
                this.handleNotLoggedIn();
                return;
            }
            if (!this.userData.is_email_verified) {
                this.handleVerifyEmail();
                return;
            }
            dataLayer.push({
                event: 'checkout',
                ecommerce: {
                    currencyCode: environment.region === 'global' ? 'USD' : this.product.currency,
                    checkout: {
                        actionField: {
                            step: 1,
                        },
                        products: [
                            {
                                name: this.product.name,
                                price: environment.region === 'global' ? this.realPrice / 1000 : this.realPrice,
                                quantity: this.quantity,
                                category: `${this.service.service_name}-${this.category.no}`,
                            },
                        ],
                    },
                },
            });
            this.buttonLoading = true;
            this.$shopAPI
                .beginTransaction(
                    this.$i18n.locale,
                    this.product.no,
                    this.product.currency,
                    this.service.service_group_code,
                    this.quantity
                )
                .then(
                    (url) => {
                        window.location.href = url;
                    },
                    ({ error_code }) => {
                        switch (error_code) {
                            case 403002:
                                this.handleNotLoggedIn();
                                break;
                            case 403004:
                                this.handleVerifyEmail();
                                break;
                            default:
                                this.$router.push({
                                    name: 'error',
                                });
                                break;
                        }
                    }
                )
                .finally(() => {
                    this.buttonLoading = false;
                });
        },
        handleNotLoggedIn() {
            this.$dialog.confirm({
                title: 'request-login.title',
                content: 'request-login.message',
                confirmLabel: 'request-login.confirm',
                confirmFunction: this.navToLogin,
                cancelLabel: 'cancel',
            });
        },
        handleVerifyEmail() {
            this.$dialog.alert({
                title: 'verify-email.title',
                message: 'verify-email.message',
            });
        },
        navToLogin() {
            window.location = `${
                config[process.env.NODE_ENV][environment.region].account
            }/login?return_url=${encodeURIComponent(window.location.href)}`;
        },
        increaseQuantity() {
            if (this.quantity >= this.product.restrictions.max_item_quantity) return;
            this.quantity++;
        },
        decreaseQuantity() {
            if (this.quantity <= 1) return;
            this.quantity--;
        },
        toggleDescription() {
            this.isDescriptionExpanded = !this.isDescriptionExpanded;
        },
        displayCurrency(amount) {
            return this.currency === 'NX'
                ? `${this.$n(amount)} ${this.currency}`
                : this.currency === 'TWD'
                ? `${this.$n(amount)} ${this.$t('shop.points')}`
                : `${this.$n(amount, 'currency', this.currency)}`;
        },
    },
};
</script>

<style lang="less" scoped>
@scrollbar-primary-color: #2f3034;
@scrollbar-secondary-color: #626266;

.product-details {
    display: flex;
    flex-direction: column;
    margin-top: 60px;
    .lightbox {
        background-color: rgba(0, 0, 0, 0.8);
        height: 100%;
        left: 0;
        padding: 10vh 20px;
        position: absolute;
        top: 0;
        width: 100%;
        .lightbox-container {
            display: flex;
            justify-content: center;
            margin: 0 auto;
            width: 100%;
            .lightbox-image {
                background-position: top;
                background-repeat: no-repeat;
                border-radius: 5px;
                display: inline-block;
                height: 75vh;
                margin-right: 10px;
                max-width: 1180px;
                width: 100%;
            }
            .lightbox-close {
                cursor: pointer;
                display: inline-block;
                height: 30px;
                width: 30px;
            }
        }
    }
    &.ieMode .details-container .cover-image {
        margin-right: 50px;
    }
    .details-container {
        display: flex;
        height: 490px;
        justify-content: space-between;
        user-select: none;
        width: 100%;
        .cover-image {
            background-position: center;
            background-repeat: no-repeat;
            border-radius: 5px;
            flex: 1;
            margin-right: 30px;
            max-width: 745px;
            overflow: hidden;
            position: relative;
            .zoom {
                align-items: center;
                background-color: rgba(0, 0, 0, 0.5);
                border-radius: 50%;
                bottom: 0;
                cursor: pointer;
                display: flex;
                height: 45px;
                justify-content: center;
                margin-bottom: 20px;
                margin-right: 20px;
                position: absolute;
                right: 0;
                width: 45px;
                &:hover {
                    background-color: rgba(51, 51, 51, 0.5);
                }
                &:active {
                    background-color: rgba(26, 26, 26, 0.5);
                }
                .zoom-icon {
                    height: 17px;
                    width: 17px;
                }
            }
        }
        .details {
            display: flex;
            flex-direction: column;
            width: 485px;
            .title {
                color: #fff;
                font-size: 30px;
                font-weight: bold;
                letter-spacing: -0.75px;
                line-height: 1.33;
                margin-bottom: 15px;
            }
            .description {
                color: #b3b3b3;
                font-size: 14px;
                height: 100%;
                line-height: 1.57;
                overflow-y: auto;
                scrollbar-arrow-color: @scrollbar-secondary-color;
                scrollbar-base-color: @scrollbar-secondary-color;
                scrollbar-color: @scrollbar-secondary-color @scrollbar-primary-color;
                scrollbar-face-color: @scrollbar-secondary-color;
                scrollbar-track-color: @scrollbar-primary-color;
                scrollbar-width: thin;
                &::-webkit-scrollbar {
                    width: 4px;
                }
                &::-webkit-scrollbar-track {
                    background-color: @scrollbar-primary-color;
                    border-radius: 5px;
                    width: 4px;
                }
                &::-webkit-scrollbar-thumb {
                    &:hover {
                        background-color: #999;
                    }
                    background-color: @scrollbar-secondary-color;
                    border-radius: 5px;
                    width: 4px;
                }
            }
            .expand-description {
                display: none;
            }
            .price {
                align-items: center;
                color: #fff;
                display: flex;
                flex-wrap: wrap;
                font-size: 28px;
                font-weight: bold;
                letter-spacing: -1.4px;
                margin-top: 25px;
                &.single-product {
                    justify-content: flex-end;
                }
                .discounted {
                    color: #999;
                    font-size: 18.5px;
                    font-weight: normal;
                    letter-spacing: -0.46px;
                    text-decoration: line-through;
                }
                .amounts {
                    align-items: center;
                    display: flex;
                    height: 60px;
                }
                .quantities {
                    display: inline-block;
                    margin-left: auto;
                    &.ieMode {
                        margin-left: 20px;
                    }
                    .quantity-container {
                        align-items: center;
                        cursor: pointer;
                        display: flex;
                        height: 45px;
                        .quantity {
                            align-items: center;
                            color: #fefefe;
                            cursor: default;
                            display: flex;
                            font-size: 16px;
                            height: 100%;
                            justify-content: center;
                            letter-spacing: -0.4px;
                            width: 45px;
                        }
                        .quantity-increase,
                        .quantity-decrease {
                            &.disabled {
                                cursor: not-allowed;
                            }
                            &:not(.disabled):hover {
                                background-color: #3c404d;
                            }
                            &:not(.disabled):active {
                                background-color: #202229;
                            }
                            align-items: center;
                            background-color: #2c2f38;
                            border-radius: 5px;
                            display: flex;
                            height: 45px;
                            justify-content: center;
                            width: 45px;
                            .quantity-icon-increase,
                            .quantity-icon-decrease {
                                height: 15px;
                                width: 15px;
                            }
                        }
                    }
                }
            }
            .discount {
                color: #f2b600;
                margin-right: 10px;
            }
            .total-price {
                border-top: 1px solid rgba(255, 255, 255, 0.2);
                display: flex;
                justify-content: space-between;
                margin-bottom: -10px;
                margin-top: 25px;
                padding-top: 30px;
                .total {
                    color: rgba(255, 255, 255, 0.8);
                    font-size: 18px;
                }
                .total-amount {
                    color: #fff;
                    font-size: 24px;
                    letter-spacing: -1.2px;
                }
            }
            button {
                background-color: #0077ff;
                border: none;
                border-radius: 5px;
                color: #fff;
                font-size: 14px;
                height: 50px;
                margin-top: 40px;
                &.disabled {
                    background-color: rgba(175, 175, 175, 0.6);
                }
                &:not(:active):not(.disabled):hover {
                    background-color: #4ca1ff;
                }
                &:not(.disabled):active {
                    background-color: #0052b3;
                    color: rgba(255, 255, 255, 0.6);
                }
            }
        }
    }
    .bundles-container {
        margin-top: 30px;
        .title {
            color: #d9d9d9;
            font-size: 18px;
            letter-spacing: -0.45px;
        }
        .bundle-list-container {
            width: 100%;
            .bundle-list-row {
                display: flex;
                flex-wrap: wrap;
                margin: -7.5px;
                margin-top: 15px;
                .bundle-container {
                    display: flex;
                    flex: 0 0 25%;
                    justify-content: center;
                    max-width: 20%;
                    padding: 7.5px;
                    .bundle {
                        overflow: hidden;
                        padding-bottom: 10px;
                        width: 100%;
                        .bundle-image {
                            background-position: top;
                            background-repeat: no-repeat;
                            background-size: cover;
                            height: 157px;
                            width: 100%;
                            position: relative;
                            &.dimmed {
                                opacity: 0.6;
                            }
                            .bundle-description {
                                background-color: rgba(0, 0, 0, 0.5);
                                border-radius: 50%;
                                bottom: 0;
                                cursor: pointer;
                                height: 30px;
                                margin-bottom: 10px;
                                margin-right: 10px;
                                position: absolute;
                                right: 0;
                                width: 30px;
                                .anchor-icon {
                                    align-items: center;
                                    display: flex;
                                    height: 30px;
                                    justify-content: center;
                                    width: 30px;
                                    .read-more-icon {
                                        height: 9px;
                                        width: 12px;
                                    }
                                }
                                &:hover {
                                    background-color: rgba(51, 51, 51, 0.5);
                                }
                                &:active {
                                    background-color: rgba(26, 26, 26, 0.5);
                                }
                                .read-more-icon {
                                    height: 9px;
                                    width: 12px;
                                }
                            }
                        }
                        .bundle-details {
                            color: #b3b3b3;
                            font-size: 13px;
                            margin-top: 15px;
                        }
                    }
                }
            }
        }
    }
}
@media (max-width: 1024px) {
    .product-details {
        margin-top: 40px;
        &.ieMode .details-container .cover-image {
            margin-right: 30px;
        }
        .details-container {
            height: 480px;
            .details {
                width: 350px;
                .title {
                    font-size: 26px;
                }
            }
        }
        .bundles-container .bundle-list-container .bundle-list-row .bundle-container {
            flex: 0 0 25%;
            max-width: 25%;
            .bundle .bundle-image {
                height: 148px;
            }
        }
    }
}
@media (max-width: 768px) {
    .product-details {
        margin-bottom: 20px;
        margin-top: 0;
        .details-container {
            flex-direction: column;
            height: 100%;
            .cover-image {
                border-radius: 0;
                flex: none;
                height: 450px;
                margin-right: 0 !important;
                max-width: none;
                .zoom {
                    display: none;
                }
            }
            .details {
                .price {
                    margin-top: 0;
                }
                .total-price {
                    margin-bottom: 30px;
                }
                margin-top: 30px;
                max-width: none;
                padding: 0 20px;
                width: 100%;
                .title {
                    margin-bottom: 20px;
                }
                .commerce {
                    margin-top: 30px;
                }
                .description {
                    max-height: 80px;
                    overflow: hidden;
                    &.expanded {
                        max-height: none;
                    }
                }
                .expand-description {
                    display: block;
                    position: relative;
                    .expand-description-icon {
                        border-left: 3.5px solid transparent;
                        border-right: 3.5px solid transparent;
                        border-top: 5px solid #b3b3b3;
                        cursor: pointer;
                        height: 0;
                        position: absolute;
                        right: 20px;
                        top: 10px;
                        width: 0;
                        &:hover {
                            border-top: 5px solid #fff;
                        }
                        &:active {
                            border-top: 5px solid #808080;
                        }
                        &.expanded {
                            border-bottom: 5px solid #b3b3b3;
                            border-left: 3.5px solid transparent;
                            border-right: 3.5px solid transparent;
                            border-top: none;
                            &:hover {
                                border-bottom: 5px solid #fff;
                                border-top: none;
                            }
                            &:active {
                                border-bottom: 5px solid #808080;
                                border-top: none;
                            }
                        }
                    }
                }
                button {
                    margin-top: 30px;
                }
            }
        }
        .bundles-container {
            margin: 50px 20px 0;
            .bundle-list-container .bundle-list-row .bundle-container {
                flex: 0 0 33.33%;
                max-width: 33.33%;
                .bundle {
                    padding-bottom: 5px;
                    .bundle-image {
                        height: 146px;
                    }
                }
            }
        }
    }
}
@media (max-width: 480px) {
    .product-details {
        .details-container {
            .cover-image {
                height: 280px;
            }
            .details .title {
                font-size: 24px;
            }
        }
        .bundles-container {
            align-items: center;
            display: flex;
            flex-direction: column;
            margin-top: 40px;
            .bundle-list-container .bundle-list-row .bundle-container {
                flex: 0 0 50%;
                max-width: 50%;
                .bundle .bundle-image {
                    height: 157px;
                }
            }
        }
    }
}
</style>
